/**
 * 本文件是窗口弹出函数模块
 * @author 笑三少
 */
import { h, ref, createApp, type Component } from 'vue'
import { PageDialog } from '@/layouts/components'

// 弹窗配置接口
export interface DialogOptions {
  title?: string
  width?: string
  height?: string
  onClose?: () => void
}

// 弹窗实例接口
export interface DialogInstance {
  close: () => void
}

// 存储当前打开的弹窗
let currentDialog: {
  app: any
  container: HTMLElement
  instance: any
} | null = null

/**
 * 打开弹窗 - 只加载页面内容，没有底部按钮
 * @param component 要显示的Vue组件
 * @param props 传递给组件的props
 * @param options 弹窗配置
 * @returns 弹窗实例
 */
export function openWindow(
  component: Component,
  props: Record<string, any> = {},
  options: DialogOptions = {}
): DialogInstance {
  // 关闭已打开的弹窗
  if (currentDialog) closeWindow()
  // 创建容器
  const container = document.createElement('div')
  document.body.appendChild(container)
  // 弹窗状态
  const visible = ref(true)
  // 默认配置
  const {
    title = '提示',
    onClose
  } = options

  // 创建弹窗组件
  const dialogComponent = {
    setup() {
      const handleClose = () => {
        if (onClose) onClose()
        visible.value = false
      }
      return () => h(PageDialog, {
        modelValue: visible.value,
        'onUpdate:modelValue': (val: boolean) => {
          visible.value = val
          if (!val) {
            setTimeout(() => {
              if (currentDialog) {
                document.body.removeChild(container)
                currentDialog = null
              }
            }, 300)
          }
        },
        title,
        onClose: handleClose
      }, {
        default: () => [
          h('div', { class: 'dialog-content' }, [
            h(component, {
              ...props,
              onClose: handleClose  // 传递关闭方法给子组件
            })
          ])
        ]
      })
    }
  }

  // 创建应用
  const app = createApp(dialogComponent)
  // 挂载
  app.mount(container)
  // 保存实例
  currentDialog = {
    app,
    container,
    instance: {
      close: () => {
        visible.value = false
      }
    }
  }
  return currentDialog.instance
}

/**
 * 关闭当前弹窗
 */
export function closeWindow() {
  if (currentDialog) {
    currentDialog.app.unmount()
    document.body.removeChild(currentDialog.container)
    currentDialog = null
  }
}
